<template>
  <div ref="rootRef" :data-test="testAttribute" :class="pSelectClasses">
    <p-text-input
      v-model="value"
      :name="name"
      :class="props.readonly && 'cursor-pointer'"
      type="text"
      :readonly="props.readonly"
      :placeholder="label"
      :error="props.error"
      @click="isFocused = true"
    >
      <!--
        When `readonly` is set, the input stays readonly the whole time —
        intentional, because the input acts purely as a click target that
        opens the dropdown. On mobile this prevents the OS keyboard from
        opening on the first tap; on desktop it disables typing into a
        non-searchable select.
      -->
      <template #append>
        <p-icon
          size="sm"
          icon="caret-down"
          color="fg-secondary"
          :class="['p-select__icon mr-2', { 'p-select__icon--reverse': isFocused }]"
        />
      </template>
    </p-text-input>

    <transition name="slide-fade-down" mode="in-out">
      <component
        :is="dropdownComponent"
        v-if="isFocused && !props.disabled"
        class="p-select__dropdown"
        :title="label"
        show-close
        @on-close="isFocused = false"
      >
        <div class="p-select__dropdown-container">
          <div v-if="props.searchable" class="p-12">
            <p-search-input v-model="search" size="md" placeholder="" />
          </div>
          <div class="p-select__content">
            <div v-for="item in filteredItems" :key="item.value" class="p-select__item">
              <label class="place-items-between p-12 cursor-pointer" @mousedown.prevent>
                <input
                  :id="item.value.toString()"
                  v-model="selectedOption"
                  type="radio"
                  :value="item.value"
                  :name="name"
                  @click.stop="handleItemClick(item)"
                />
                <span class="f-utility-14 t-fg-primary" v-text="item.label" />
                <p-icon v-if="item.value === selectedOption" size="sm" icon="check" />
              </label>
              <p-divider class="mx-12 my-2" />
            </div>
          </div>
        </div>
      </component>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { useField } from 'vee-validate'

const { $device } = useNuxtApp()

interface PSelect {
  value: string | number
  label: string
}

const emit = defineEmits(['on-change'])

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    default: '',
  },
  items: {
    type: Array as PropType<Array<PSelect>>,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  selectedOption: {
    type: String,
    default: '',
  },
  searchable: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
})

const rootRef = ref<HTMLElement | null>(null)
const testAttribute = dataTest(props.name)
const isFocused = ref(false)
const search = ref('')
const selectedOption = ref(props.selectedOption)
const name = toRef(props, 'name')

const modelValue = defineModel<string>('modelValue')

const { value } = useField(name, undefined, {
  initialValue: modelValue.value,
})

const filteredItems = computed(() => {
  if (search.value) {
    return props.items.filter((item) => {
      const searchString = turkishToEnglish(item?.label + item?.value).toLowerCase?.()
      return searchString.includes(turkishToEnglish(search?.value?.toLowerCase?.()))
    })
  }
  return props.items
})

const pSelectClasses = computed<Record<string, boolean>>(() => {
  return {
    'p-select': true,
    'p-select--focused': isFocused.value,
    'p-select--disabled': props.disabled,
  }
})

const dropdownComponent = computed(() => {
  return $device.isDesktop ? 'div' : resolveComponent('p-bottom-sheet')
})

watch(
  () => value.value,
  () => (modelValue.value = value.value),
)

watch(
  () => modelValue.value,
  () => (value.value = modelValue.value || ''),
)

function handleItemClick(item: PSelect): void {
  const scrollY = window.scrollY
  selectedOption.value = String(item.value)
  value.value = item.label
  isFocused.value = false
  emit('on-change', item)
  nextTick(() => {
    window.scrollTo({ top: scrollY })
    requestAnimationFrame(() => {
      window.scrollTo({ top: scrollY })
    })
  })
}

onMounted(() => {
  document.addEventListener('click', handleGlobalClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleGlobalClick)
})

function handleGlobalClick(event: MouseEvent) {
  if (!$device.isDesktop) return

  const target = event.target as Node
  if (rootRef.value && !rootRef.value.contains(target)) {
    isFocused.value = false
  }
}
</script>
